import axios from "axios";
import authService from "./authService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const API_BASE = `${BACKEND_URL}/payments`;

/**
 * Payment Category Service
 * Gerencia todas as operações de categorias de pagamento
 */
class PaymentCategoryService {
  /**
   * Get authorization headers with JWT token
   * @returns {object} Headers object
   */
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Get all payment categories
   * @returns {Promise<Array>}
   */
  async getAllCategories() {
    try {
      const response = await axios.get(API_BASE, this.getAuthHeaders());
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar categorias de pagamento:", error);
      throw error;
    }
  }

  /**
   * Get payment category by ID
   * @param {number} id - Category ID
   * @returns {Promise<object>}
   */
  async getCategoryById(id) {
    try {
      const response = await axios.get(
        `${API_BASE}/${id}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao buscar categoria ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new payment category
   * @param {object} categoryData - Category data
   * @returns {Promise<object>}
   */
  async createCategory(categoryData) {
    try {
      this.validateCategoryData(categoryData);

      const response = await axios.post(
        API_BASE,
        categoryData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao criar categoria:", error);
      throw error;
    }
  }

  /**
   * Update payment category
   * @param {number} id - Category ID
   * @param {object} categoryData - Updated category data
   * @returns {Promise<object>}
   */
  async updateCategory(id, categoryData) {
    try {
      this.validateCategoryData(categoryData);

      const response = await axios.put(
        `${API_BASE}/${id}`,
        categoryData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao atualizar categoria ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete payment category
   * @param {number} id - Category ID
   * @returns {Promise<void>}
   */
  async deleteCategory(id) {
    try {
      await axios.delete(`${API_BASE}/${id}`, this.getAuthHeaders());
    } catch (error) {
      console.error(`❌ Erro ao deletar categoria ${id}:`, error);
      throw error;
    }
  }

  /**
   * Validate category data
   * @param {object} data - Category data to validate
   * @throws {Error} If validation fails
   */
  validateCategoryData(data) {
    const errors = [];

    if (!data.name || data.name.trim() === "") {
      errors.push("Nome da categoria é obrigatório");
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }
  }

  /**
   * Format date
   * @param {string} date - Date string
   * @returns {string}
   */
  formatDate(date) {
    if (!date) return "-";
    return new Date(date).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}

export default new PaymentCategoryService();
